/**
 * Terminal payment diagnostics.
 *
 * Two jobs, both in service of the same thing: letting a property answer
 * "what did you send my terminal, and what came back" without escalating.
 *
 *   fetchPaymentDiagnostics       reads the allowlisted exchange for a payment
 *   recordTerminalTransportFailure records an attempt that never reached the
 *                                 gateway at all
 *
 * The second exists because Shift4's UTG runs on the property's own LAN and
 * this browser posts to it directly. When that fetch rejects - UTG process
 * down, self-signed cert, wrong port, https page to an http UTG - the response
 * never reaches our server, so nothing is written to PAYMENTS and nothing
 * appears in the folio log. The operator sees a toast and that is the entire
 * record of the attempt. Nine call sites had that hole independently; they now
 * share this one.
 */
import {
  djangoHandleDataRequests,
  djangoHandleDataMutationRequest,
} from "../api";
import { diagnoseUtgFailure } from "./utgProbe";

/**
 * Normalise the several shapes a failed UTG call rejects with.
 *
 * A network-level rejection is a bare TypeError with "Failed to fetch" and no
 * response at all - which is precisely the case we could not see before, so it
 * is the one worth naming clearly. A UTG that answered with an error gives us
 * its own text, which is far more useful and is preferred when present.
 */
export function describeTransportError(error) {
  const utgError = error?.json?.result?.[0]?.error;
  if (utgError?.longText) {
    return {
      message: utgError.longText,
      http_status: error?.status ?? null,
      reached_terminal: true,
    };
  }

  if (error?.status) {
    return {
      message: `Terminal service returned HTTP ${error.status}`,
      http_status: error.status,
      reached_terminal: true,
    };
  }

  return {
    message:
      error?.message === "Failed to fetch"
        ? "Could not reach the card terminal service on this network"
        : error?.message || "The card terminal did not respond",
    http_status: null,
    reached_terminal: false,
  };
}

/**
 * Record an attempt that never reached the gateway.
 *
 * Deliberately never rejects. It is called from inside an error handler that
 * has already told the operator what went wrong; turning a bookkeeping failure
 * into a second error toast would make a bad moment worse and teach people to
 * ignore the first one.
 */
export async function recordTerminalTransportFailure({
  hotelId,
  bookingId,
  reservationId,
  amount,
  transType = "sale",
  gateway,
  terminalId,
  gatewayRequest,
  loggedUserEmail,
  error,
  diagnosis,
  endpoint,
}) {
  try {
    const res = await djangoHandleDataMutationRequest(
      "POST",
      "invoice/recordTerminalAttempt/",
      {
        hotelId,
        bookingID: bookingId,
        reservation_id: reservationId,
        amount,
        trans_type: transType,
        gateway,
        terminal_id: terminalId,
        gateway_request: gatewayRequest,
        logged_user_email: loggedUserEmail,
        transport_error: {
          ...describeTransportError(error),
          // The URL actually dialled. Server-side this is recorded onto the
          // attempt so a support ticket can name the exact endpoint.
          endpoint,
        },
        diagnosis,
      }
    );
    return res?.payment_id ?? null;
  } catch (recordingError) {
    // Nothing useful to do here: we are already in the failure path and the
    // operator has been told. Leave a console breadcrumb for a support session.
    console.error("Could not record terminal attempt", recordingError);
    return null;
  }
}

/**
 * Call-site shorthand for the Shift4 UTG failure handlers.
 *
 * Every one of them already has the outgoing `data` payload and the generated
 * UTG request in scope, and every one of them differs slightly in the
 * surrounding state. Taking the payload wholesale keeps each call site to a
 * single line, which is the only reason this stayed reviewable across twelve
 * of them.
 */
export async function recordShift4TransportFailure(
  data,
  gatewayRequest,
  error,
  utgUrl,
  priorAttemptMs,
  endpoint,
  inFlightProbe
) {
  // Work out WHY before recording, so the folio log carries a diagnosis
  // rather than just "the terminal did not respond".
  //
  // Prefer the probe started alongside the charge. It ran BEFORE the charge
  // failed, so it measured the network rather than Chrome's cache of that
  // failure - strictly better evidence than anything gathered now, and it
  // spares the property's LAN a second round of probing.
  //
  // Only when it found a fault, though. If it said the terminal was fine and
  // the charge failed anyway, events have contradicted it: either something
  // changed mid-charge or the fault was never at the transport. Re-probe
  // rather than record a stale "all fine".
  let diagnosis = null;
  try {
    const cold = inFlightProbe ? await inFlightProbe : null;
    diagnosis = cold?.code ? cold : await diagnoseUtgFailure({
      utgUrl,
      error,
      responseBody: error?.json,
      // How long the charge itself ran before failing. A probe that comes
      // back fast after a request that hung is reading Chrome's cache, not
      // the network - see PRIOR_ATTEMPT_HUNG_MS in utgProbe.
      priorAttemptMs,
    });
  } catch (probeError) {
    // A probe that fails tells us nothing, which is no worse than not having
    // probed. Never let it stop the attempt being recorded.
    diagnosis = null;
  }

  return recordTerminalTransportFailure({
    diagnosis,
    hotelId: data?.hotelId,
    bookingId: data?.bookingID,
    reservationId: data?.reservation_id,
    amount: data?.amount,
    transType: data?.trans_type,
    gateway: "SHIFT4",
    terminalId: data?.terminal_id,
    gatewayRequest,
    loggedUserEmail: data?.logged_user_email,
    error,
    endpoint,
  });
}

/** Read the allowlisted request/response for one payment. */
export async function fetchPaymentDiagnostics(hotelId, paymentId) {
  return djangoHandleDataRequests(
    `invoice/paymentDiagnostics/?hotelId=${encodeURIComponent(
      hotelId
    )}&payment_id=${encodeURIComponent(paymentId)}`
  );
}

/**
 * True when a folio log row is a payment we can show diagnostics for.
 *
 * Rows written before the payment id existed carry -1. That is ordinary, not
 * an error - notably the auth-hold half of a charge/hold pair - so the caller
 * shows nothing rather than an empty panel.
 */
/**
 * Gateways that can drive a physical card terminal, and therefore have an
 * exchange worth showing. Each has a diagnostics class in
 * core/hotelAdmin/payments/diagnostics/gateways.py.
 *
 * STRIPE belongs here: Stripe Terminal is real and StripeTerminalDiagnostics
 * maps its `reader` to terminal_id, which is what the server's
 * is_terminal_payment() recognises. Only a couple of properties use it today,
 * which is exactly why dropping it would have gone unnoticed.
 */
const TERMINAL_CAPABLE_GATEWAYS = [
  "FORTIS",
  "SHIFT4",
  "ELAVON",
  "STRIPE",
  "FREEDOMPAY",
];

export function folioLogHasDiagnostics(row) {
  if (
    row?.log_sub_component_type !== "ONLINE_PAYMENT" ||
    !row?.log_sub_component_value ||
    String(row.log_sub_component_value) === "-1"
  ) {
    return false;
  }
  const text = String(row.log_text || "");

  // Failures only. A settled charge has nothing to diagnose, and offering the
  // control on every payment row put it on more lines than it helped.
  if (!/status\s*:\s*failed/i.test(text)) return false;

  // Gateways that can drive a physical terminal. Anything else - Razorpay,
  // TSYS, Airpay - has no terminal to diagnose, so the control there only
  // ever opened "Diagnostics are available for card terminal payments only."
  //
  // Keyed on the gateway rather than the "EMV " marker in the log text. The
  // marker is precise where it appears, but payment_gateway.py adds it only
  // when the caller passes is_emv == "YES", and is_emv is read in exactly two
  // adapters: fortis_pg and shift4_pg. Gating on it would have silently
  // hidden Elavon and Stripe Terminal failures - and a hidden diagnostic is
  // worse than a dead end, because nobody can see that it is missing.
  //
  // The cost is that an ONLINE failure on a terminal-capable gateway still
  // reaches the dead end. That is the status quo, it is bounded, and it errs
  // in the recoverable direction.
  const gateway = /gateway\s*:\s*([A-Za-z0-9_]+)/i.exec(text);
  return TERMINAL_CAPABLE_GATEWAYS.includes(
    (gateway ? gateway[1] : "").toUpperCase()
  );
}
